import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ScrollBehaviorProvider } from "./ScrollBehaviorContext";

// Contextos criados aqui mesmo
export const UserContext = createContext();
export const CartContext = createContext();
export const CatalogCartContext = createContext();
export const AdminContext = createContext();
export const AdminOrdersDataContext = createContext();
export const DashboardContext = createContext();
export const NotificationContext = createContext();
export const StarboxContext = createContext();
export const PackagingContext = createContext();

export const IslandContext = createContext();

function ComposeProviders({ providers, children }) {
  return providers.reduceRight(
    (acc, [Provider, value]) => <Provider value={value}>{acc}</Provider>,
    children
  );
}

export default function AppProviders({ children }) {
  // User
  const [user, setUser] = useState({});
  const [newUser, setNewUser] = useState(null);
  const [chatting, setChatting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState(null);

  // Cart
  const [cart, setCart] = useState([]);
  const [fullCartWindowState, setFullCartWindowState] = useState(null);
  const [fullCartMeta, setFullCartMeta] = useState(null);
  const [fullCartAutomationState, setFullCartAutomationState] = useState(null);
  const [fullCartDraft, setFullCartDraft] = useState({});
  const [cartOpenSources, setCartOpenSources] = useState({});
  const [cartSearch, setCartSearch] = useState("");
  const [catalogSelected, setCatalogSelected] = useState([]);
  const [catalogMarketplaceId, setCatalogMarketplaceId] = useState(null);

  // Produtos e integrações
  const [products, setProducts] = useState([]);
  const [packagings, setPackagings] = useState([]);
  const [packagingsLoaded, setPackagingsLoaded] = useState(false);
  const [integrations, setIntegrations] = useState({});
  const [marketplaces, setMarketplaces] = useState({});

  // Orders e pagamentos
  const [orders, setOrders] = useState({});
  const [payments, setPayments] = useState([]);
  const [orders10Days, setOrders10Days] = useState(null);
  const [bootstrapMeta, setBootstrapMeta] = useState({
    orders: null,
    products: null,
    analyticsDeferred: false,
    analyticsLoaded: false,
  });
  const [ordersSearchState, setOrdersSearchState] = useState({
    query: "",
    orders: [],
    pagination: null,
  });
  const [productsSearchState, setProductsSearchState] = useState({
    query: "",
    products: [],
    pagination: null,
  });
  const [platformConfig, setPlatformConfig] = useState({});
  const [wsSendJson, setWsSendJson] = useState(() => () => false);

  // Dashboard
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardOrders, setDashboardOrders] = useState(null);
  const [capitalData, setCapitalData] = useState(null);
  const [adsData30, setAdsData30] = useState(null);
  const [period, setPeriod] = useState(0);
  const [selectedMarketplace, setSelectedMarketplace] = useState(null);
  const [activeMarketplaceIds, setActiveMarketplaceIds] = useState([]);
  const [adsData, setAdsData] = useState(null);
  const [fixedCosts, setFixedCosts] = useState([]);
  const [fixedCostEntries, setFixedCostEntries] = useState([]);
  const [monthlyCosts, setMonthlyCosts] = useState([]);

  // Admin
  const [adminProducts, setAdminProducts] = useState([]);
  const [adminBoxes, setAdminBoxes] = useState([]);
  const [adminGroups, setAdminGroups] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminIntegrations, setAdminIntegrations] = useState([]);
  const [adminOrders, setAdminOrders] = useState([]);
  const [adminPayments, setAdminPayments] = useState([]);
  const [adminMaterials, setAdminMaterials] = useState([]);
  const [adminSuppliers, setAdminSuppliers] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [config, setConfig] = useState(null);
  const [adminPrintingConfig, setAdminPrintingConfig] = useState({});
  const [printerStatuses, setPrinterStatuses] = useState({});
  const [printerNames, setPrinterNames] = useState([]);
  const [developerMode, setDeveloperMode] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("adminDeveloperMode") === "true";
  });

  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);
  const [adminRealtimeConnected, setAdminRealtimeConnected] = useState(false);

  const toIdMap = (collection) => {
    if (!collection) return {};
    if (Array.isArray(collection)) {
      return collection.reduce((acc, item) => {
        if (item && item.id !== undefined) acc[item.id] = item;
        return acc;
      }, {});
    }
    if (typeof collection === "object") return { ...collection };
    return {};
  };

  const productsById = useMemo(() => toIdMap(products), [products]);
  const packagingsById = useMemo(() => toIdMap(packagings), [packagings]);
  const integrationsById = useMemo(() => toIdMap(integrations), [integrations]);
  const ordersById = useMemo(() => toIdMap(orders), [orders]);

  const adminProductsById = useMemo(
    () => toIdMap(adminProducts),
    [adminProducts]
  );
  const adminUsersById = useMemo(() => toIdMap(adminUsers), [adminUsers]);
  const adminOrdersById = useMemo(() => toIdMap(adminOrders), [adminOrders]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("adminDeveloperMode", developerMode ? "true" : "false");
  }, [developerMode]);

  // Starbox
  const [starboxSlots, setStarboxSlots] = useState(null);
  const [starboxTasks, setStarboxTasks] = useState(null);
  const [starboxToday, setStarboxToday] = useState(null);
  const [starboxStatus, setStarboxStatus] = useState({
    x: 0,
    y: 0,
    z: 0,
    status: "Offline",
  });

  // Island
  const hidden = false;
  const setHidden = useCallback(() => {}, []);
  const [isFocused, setIsFocused] = useState(false);
  const [islandText, setIslandText] = useState("");
  const [islandLoading, setIslandLoading] = useState(false);
  const [meliIntegrationSwitcherState, setMeliIntegrationSwitcherState] =
    useState(null);
  const [unifiedMarketplaceSheetMode, setUnifiedMarketplaceSheetMode] =
    useState(null);

  // Notificações
  const [notificationList, setNotificationList] = useState([]);
  const notificationListRef = useRef(notificationList);

  // Modais
  const modals = useRef({});

  // ---- Valores memoizados por contexto ----

  const setIsOpen = useCallback((value, source = "default") => {
    setCartOpenSources((prev) => {
      const already = !!prev[source];
      if (value && already) return prev;
      if (!value && !already) return prev;
      const next = { ...prev };
      if (value) {
        next[source] = true;
      } else {
        delete next[source];
      }
      return next;
    });
  }, []);

  const isOpen = useMemo(
    () => Object.keys(cartOpenSources).length > 0,
    [cartOpenSources]
  );

  const cartValue = useMemo(
    () => ({
      cart,
      setCart,
      isOpen,
      setIsOpen,
      cartSearch,
      setCartSearch,
      fullCartWindowState,
      setFullCartWindowState,
      fullCartMeta,
      setFullCartMeta,
      fullCartAutomationState,
      setFullCartAutomationState,
      fullCartDraft,
      setFullCartDraft,
    }),
    [
      cart,
      isOpen,
      cartSearch,
      setIsOpen,
      fullCartWindowState,
      fullCartMeta,
      fullCartAutomationState,
      fullCartDraft,
    ]
  );

  const clearCatalogCart = useCallback(() => {
    setCatalogSelected([]);
    setCatalogMarketplaceId(null);
  }, []);

  const catalogCartValue = useMemo(
    () => ({
      selected: catalogSelected,
      setSelected: setCatalogSelected,
      marketplaceId: catalogMarketplaceId,
      setMarketplaceId: setCatalogMarketplaceId,
      clearCart: clearCatalogCart,
    }),
    [
      catalogSelected,
      catalogMarketplaceId,
      clearCatalogCart,
    ]
  );

  const userValue = useMemo(
    () => ({
      user,
      setUser,
      chatting,
      setChatting,
      messages,
      setMessages,
      newMessage,
      setNewMessage,
      products,
      setProducts,
      integrations,
      setIntegrations,
      integrationsById,
      marketplaces,
      setMarketplaces,
      orders,
      setOrders,
      ordersById,
      payments,
      setPayments,
      bootstrapMeta,
      setBootstrapMeta,
      ordersSearchState,
      setOrdersSearchState,
      productsSearchState,
      setProductsSearchState,
      platformConfig,
      setPlatformConfig,
      wsSendJson,
      setWsSendJson,
      productsById,
      isRealtimeConnected,
      setIsRealtimeConnected,

      hidden,
      setHidden,
      modals,
    }),
    [
      user,
      chatting,
      messages,
      newMessage,
      products,
      productsById,
      integrations,
      integrationsById,
      marketplaces,
      orders,
      ordersById,
      payments,
      bootstrapMeta,
      ordersSearchState,
      productsSearchState,
      platformConfig,
      wsSendJson,
      isRealtimeConnected,
      hidden,
      modals,
    ]
  );

  const dashboardValue = useMemo(
    () => ({
      dashboardData,
      setDashboardData,
      dashboardOrders,
      setDashboardOrders,
      capitalData,
      setCapitalData,
      orders10Days,
      setOrders10Days,
      adsData30,
      setAdsData30,
      period,
      setPeriod,
      selectedMarketplace,
      setSelectedMarketplace,
      activeMarketplaceIds,
      setActiveMarketplaceIds,
      adsData,
      setAdsData,
      fixedCosts,
      setFixedCosts,
      fixedCostEntries,
      setFixedCostEntries,
      monthlyCosts,
      setMonthlyCosts,
    }),
    [
      dashboardData,
      dashboardOrders,
      capitalData,
      orders10Days,
      adsData30,
      period,
      selectedMarketplace,
      activeMarketplaceIds,
      adsData,
      fixedCosts,
      fixedCostEntries,
      monthlyCosts,
    ]
  );

  const packagingValue = useMemo(
    () => ({
      packagings,
      setPackagings,
      packagingsById,
      packagingsLoaded,
      setPackagingsLoaded,
    }),
    [packagings, packagingsById, packagingsLoaded]
  );

  const adminValue = useMemo(
    () => ({
      adminProducts,
      setAdminProducts,
      adminBoxes,
      setAdminBoxes,
      adminGroups,
      setAdminGroups,
      adminUsers,
      setAdminUsers,
      adminOrders,
      setAdminOrders,
      adminPayments,
      setAdminPayments,
      adminMaterials,
      setAdminMaterials,
      adminSuppliers,
      setAdminSuppliers,
      adminIntegrations,
      setAdminIntegrations,
      adminProductsById,
      adminUsersById,
      adminOrdersById,
      onlineUsers,
      setOnlineUsers,
      config,
      setConfig,
      newUser,
      setNewUser,
      adminRealtimeConnected,
      setAdminRealtimeConnected,
      adminPrintingConfig,
      setAdminPrintingConfig,
      printerStatuses,
      setPrinterStatuses,
      printerNames,
      setPrinterNames,
      developerMode,
      setDeveloperMode,
      marketplaces,
      setMarketplaces,
    }),
    [
      adminProducts,
      adminProductsById,
      adminBoxes,
      adminGroups,
      adminUsers,
      adminUsersById,
      adminOrders,
      adminOrdersById,
      adminPayments,
      adminMaterials,
      adminSuppliers,
      adminIntegrations,
      onlineUsers,
      config,
      newUser,
      adminRealtimeConnected,
      adminPrintingConfig,
      printerStatuses,
      printerNames,
      developerMode,
      marketplaces,
    ]
  );

  const adminOrdersDataValue = useMemo(
    () => ({
      adminOrders,
      adminUsers,
      adminProducts,
      adminBoxes,
      adminMaterials,
      marketplaces,
    }),
    [adminOrders, adminUsers, adminProducts, adminBoxes, adminMaterials, marketplaces]
  );

  const notificationValue = useMemo(
    () => ({
      notificationList,
      setNotificationList,
      notificationListRef,
    }),
    [notificationList]
  );

  const starboxValue = useMemo(
    () => ({
      starboxSlots,
      setStarboxSlots,
      starboxTasks,
      setStarboxTasks,
      starboxToday,
      setStarboxToday,
      starboxStatus,
      setStarboxStatus,
    }),
    [starboxSlots, starboxTasks, starboxToday, starboxStatus]
  );

  const islandValue = useMemo(
    () => ({
      hidden,
      setHidden,
      isFocused,
      setIsFocused,
      islandText,
      setIslandText,
      islandLoading,
      setIslandLoading,
      meliIntegrationSwitcherState,
      setMeliIntegrationSwitcherState,
      unifiedMarketplaceSheetMode,
      setUnifiedMarketplaceSheetMode,
    }),
    [
      hidden,
      isFocused,
      islandText,
      islandLoading,
      meliIntegrationSwitcherState,
      unifiedMarketplaceSheetMode,
    ]
  );

  const providers = [
    [CartContext.Provider, cartValue],
    [CatalogCartContext.Provider, catalogCartValue],
    [UserContext.Provider, userValue],
    [PackagingContext.Provider, packagingValue],
    [DashboardContext.Provider, dashboardValue],
    [AdminOrdersDataContext.Provider, adminOrdersDataValue],
    [AdminContext.Provider, adminValue],
    [NotificationContext.Provider, notificationValue],
    [StarboxContext.Provider, starboxValue],
    [IslandContext.Provider, islandValue],
  ];

  return (
    <ScrollBehaviorProvider>
      <ComposeProviders providers={providers}>{children}</ComposeProviders>
    </ScrollBehaviorProvider>
  );
}
